/** @jsxImportSource @emotion/react */

import React, { useMemo } from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import TextBlock from '@component/common/block/text-block';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import { TIyagigoSubMenu } from '@datatype/iyagigo/type';
import { actions as navigationActions, DEFAULT_IYAGIGO_SUB_MENU } from '@store/slice/navigation-slice';
import { useCurrentSubMenu } from '@hook/iyagigo/useSync';
import { EApiLanguageCode, EApiAttendanceType, EApiStudyType } from '@datatype/iyagigo/api-type';
import Calendar from '@design-system/calendar';
import { useAttendanceQuery, useDailyStudyRecordsQuery } from '@hook/iyagigo/useApi';
import { useStudentKeyQuery } from '@hook/iyagigo/useApi';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import WorkbookTemplate from '@component/iyagigo/common/workbook-template';
import TextButton from '@design-system/text-button';
import { ETheme } from '@datatype/type';
import Gap from '@component/common/gap';
import { useNavigate } from 'react-router-dom';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { useMySubscriptionQuery } from '@hook/billing/useApi';
import { EApiSubscriptionStatus } from '@datatype/billing/api-type';
import { actions as modalActions } from '@store/slice/modal-slice';

// ############### variable ###############
const STUDIO_SUB_MENU_IDS = ['statistic', 'configuration'] as const;

const StudioDescriptionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${size.MARGIN_1};
  padding: ${size.MARGIN_1};
  margin: 0px 0px 0px 0px;
  background-color: ${color.BROWN_4};
  width: 100%;
`;

const dateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const formatStudyTime = (milliseconds: number) => {
  const seconds = Math.floor(milliseconds / 1000);
  if (seconds < 60) return `${seconds}초`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}분${seconds % 60 ? ` ${seconds % 60}초` : ''}`;
  return `${Math.floor(minutes / 60)}시간${minutes % 60 ? ` ${minutes % 60}분` : ''}`;
};

// TODO: 출석 쿼리 캐시
const StudioHome = () => {
  // ############### route ###############
  const navigate = useNavigate();

  // ############### state ###############
  const dispatch = useTypedDispatch();
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const studioSubMenu = useCurrentSubMenu(STUDIO_SUB_MENU_IDS, DEFAULT_IYAGIGO_SUB_MENU.studio as 'statistic');
  const isStatisticSubMenu = studioSubMenu === 'statistic';
  const subscriptionQuery = useMySubscriptionQuery();
  const isSubscriber = subscriptionQuery.data?.data?.status === EApiSubscriptionStatus.ACTIVE;

  const TODAY_TAB: { id: string; name: string }[] = [
    { id: 'statistic', name: '학습통계' },
    { id: 'configuration', name: '학습설정' },
  ];

  const today = new Date();
  const todayKey = dateKey(today);
  const monthRecordsQuery = useDailyStudyRecordsQuery(
    studyLanguage ?? EApiLanguageCode.JAPANESE,
    dateKey(new Date(today.getFullYear(), today.getMonth(), 1)),
    todayKey,
  );
  const monthRecords = (monthRecordsQuery.data?.data ?? []).filter(
    (record) =>
      record.studyType !== EApiStudyType.AUTHENTIC_EXPRESSION && record.studyType !== EApiStudyType.MINI_STORY,
  );
  const summaryRows = [
    { label: '오늘', records: monthRecords.filter((record) => record.studyDate === todayKey) },
    { label: '이번 달', records: monthRecords },
  ];
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? '';
  const studentQuery = useStudentKeyQuery();
  const vocabType =
    studyLanguage === EApiLanguageCode.ENGLISH
      ? EApiAttendanceType.ENGLISH_CLOZE_VOCABULARY
      : EApiAttendanceType.JAPANESE_CLOZE_VOCABULARY;
  const grammarType =
    studyLanguage === EApiLanguageCode.ENGLISH
      ? EApiAttendanceType.ENGLISH_CLOZE_GRAMMAR
      : EApiAttendanceType.JAPANESE_CLOZE_GRAMMAR;
  // 구 enum (CLOZE_ 접두사 이전 기록) — 기존 출석도 달력에 계속 표시되어야 한다
  const legacyVocabType =
    studyLanguage === EApiLanguageCode.ENGLISH
      ? EApiAttendanceType.ENGLISH_VOCABULARY
      : EApiAttendanceType.JAPANESE_VOCABULARY;
  const legacyGrammarType =
    studyLanguage === EApiLanguageCode.ENGLISH
      ? EApiAttendanceType.ENGLISH_GRAMMAR
      : EApiAttendanceType.JAPANESE_GRAMMAR;
  const vocabQuery = useAttendanceQuery(studentKey, vocabType, { enabled: !!isAuthenticated });
  const grammarQuery = useAttendanceQuery(studentKey, grammarType, { enabled: !!isAuthenticated });
  const legacyVocabQuery = useAttendanceQuery(studentKey, legacyVocabType, { enabled: !!isAuthenticated });
  const legacyGrammarQuery = useAttendanceQuery(studentKey, legacyGrammarType, { enabled: !!isAuthenticated });
  const attendanceMarks = useMemo(() => {
    const toDates = (rows?: { attendedAt: string }[]) => (rows || []).map((a) => new Date(a.attendedAt).toDateString());
    const uniqueDates = Array.from(
      new Set([
        ...toDates(vocabQuery.data?.data),
        ...toDates(grammarQuery.data?.data),
        ...toDates(legacyVocabQuery.data?.data),
        ...toDates(legacyGrammarQuery.data?.data),
      ]),
    );
    return uniqueDates.map((d) => new Date(d));
  }, [vocabQuery.data?.data, grammarQuery.data?.data, legacyVocabQuery.data?.data, legacyGrammarQuery.data?.data]);
  const studentCreatedAt = studentQuery.data?.data?.createdAt ? new Date(studentQuery.data.data.createdAt) : undefined;

  let StudyDescription;

  if (isStatisticSubMenu) {
    StudyDescription =
      monthRecordsQuery.isPending || monthRecordsQuery.isError ? (
        <TextBlock
          text={monthRecordsQuery.isError ? '학습기록을 불러오지 못했어요.' : '학습기록을 불러오고 있어요.'}
          design={{ text: { size: 'small', color: color.BLACK_5 }, box: { alignX: 'left' } }}
        />
      ) : (
        summaryRows.map(({ label, records }) => (
          <TextBlock
            key={label}
            text={`${label} <strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">${records.reduce((sum, record) => sum + record.studyCount, 0)}개</strong>를 <strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">${formatStudyTime(records.reduce((sum, record) => sum + record.studyTime, 0))}</strong> 동안 학습했어요`}
            design={{
              text: { size: 'medium', color: color.BLACK_2 },
              box: { alignX: 'left', width: '100%' },
            }}
          />
        ))
      );
  } else if (studioSubMenu === 'configuration') {
    StudyDescription = (
      <TextBlock
        text={`<strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">학습목표</strong>와 <strong style="color: ${color.HIGHLIGHT_TEXT_COLOR};">난이도</strong>를 설정하고, 나에게 맞는 학습을 시작해보세요`}
        design={{
          text: { size: 'medium', color: color.BLACK_2, bold: false },
          box: { alignX: 'left', margin: ` 0 0 0` },
        }}
      />
    );
  }

  // ############### handler ###############
  const handleTabClick = (studioSubMenu: TIyagigoSubMenu['studio']) =>
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'studio', subMenu: studioSubMenu }));

  const handleGoButtonClick = () => {
    if (studioSubMenu === 'configuration') {
      if (!isSubscriber) {
        dispatch(modalActions.showWallToast({ type: 'subscription-wall', data: { trigger: 'access' } }));
        return;
      }
      return navigate(IYAGIGO_PATH.STUDIO_CONFIGURATION_PATH);
    }
    if (studioSubMenu === 'statistic') return navigate(IYAGIGO_PATH.STUDIO_STATISTIC_PATH);
  };

  // 월화수목금 <- 공부한날 표시하기
  // 주간 몇개 풀었음
  return (
    <WorkbookTemplate
      tabs={TODAY_TAB}
      selectedTabId={studioSubMenu!}
      onTabClick={(id) => handleTabClick(id as TIyagigoSubMenu['studio'])}>
      {isStatisticSubMenu ? (
        <TextBlock
          text={`${studyLanguage === EApiLanguageCode.ENGLISH ? '영어' : '일본어'} 학습통계`}
          design={{
            text: { size: 'medium', color: color.BLACK_2, bold: true },
            box: { alignX: 'left' },
          }}
        />
      ) : (
        <TextBlock
          text={`학습환경 설정`}
          design={{
            text: { size: 'medium', color: color.BLACK_2, bold: true },
            box: { alignX: 'left' },
          }}
        />
      )}
      <Gap height={size.MARGIN_1} />
      {isStatisticSubMenu && (
        <>
          <Calendar
            marks={attendanceMarks}
            startDate={studentCreatedAt}
            size={'medium'}
            calendarType='attendance'
            width={'100%'}
          />
        </>
      )}
      <Gap height={size.MARGIN_1} />
      <StudioDescriptionContainer>{StudyDescription}</StudioDescriptionContainer>
      <Gap height={size.MARGIN_2} />
      <TextButton
        text={'자세히 보러가기'}
        theme={ETheme.BROWN}
        size={'medium'}
        shape='pill'
        width={IS_IYAGIGO_MOBILE_VIEW ? '100%' : '200px'}
        handleClick={handleGoButtonClick}
      />
      <Gap height={size.MARGIN_1} />
    </WorkbookTemplate>
  );
};

export default StudioHome;
